// src/Components/IdentityCard/IdentityCard.tsx
//
// DEV-1039 / DEV-1042. Shared identity preview card.
//
// Used in any context where an admin selects a user before granting authority
// (project Owners & Admins tray, role slot assignment tray, future
// community promotion flows). Surfaces face + name + email + home community
// + impact signature so the admin can confirm identity before acting.
//
// Layout locked with Skip (Variant E, borderless panes):
//   ┌─────────────────────────────────┐
//   │      Jane Walker  [no2fa]        │  ← banner
//   │   jane.walker@example.org        │
//   │  📍 Reno Rotary · Home Community │
//   ├─────────────────────────────────┤
//   │     ┌──────┐    ┌──────┐         │
//   │     │ 112  │    │ RADAR│         │
//   │     │ AVT  │    │ ~120 │         │
//   │     └──────┘    └──────┘         │
//   │                  [Change person] │  ← only when onClear passed
//   └─────────────────────────────────┘

import type { SecurityUserResponse } from "../../api/security";
import { mediaSrc } from "../../api/mediaUrl";
import { normalizeGuid } from "../../utils/guid";
import ImpactSignatureMini from "../DimensionRadar/ImpactSignatureMini";
import styles from "./IdentityCard.module.css";

// ── Helpers (exported for use by callers that need them) ─────────

/**
 * Resolve a user's home community display name by looking up
 * `primaryLocationEntityGuid` in the provided map (built by the parent from
 * apiCommunityData.communityEntities). Returns null when the user has no
 * primary location set, or when the lookup misses (community filtered out
 * or stale map). Caller falls back to a localized "no home community" line.
 */
export function resolveHomeCommunityName(
  user: SecurityUserResponse | null,
  communityNameByGuid?: Map<string, string>,
): string | null {
  if (!user?.primaryLocationEntityGuid) return null;
  const key = normalizeGuid(user.primaryLocationEntityGuid);
  return communityNameByGuid?.get(key) ?? null;
}

/**
 * Compute monogram initials from a display name. Used as the placeholder
 * avatar when the user has no profile image. First + last initial for
 * multi-word names; first two letters for single-word names; "?" for empty.
 */
export function computeInitials(displayName: string): string {
  const trimmed = displayName.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Extract a first name from a display name. Returns the first whitespace-
 * delimited token, or the whole trimmed string if there's only one token.
 * Falls back to empty string when input is blank. Used for the templated
 * default invitation message — addresses the recipient by first name only
 * to keep the greeting personal but compact.
 */
export function extractFirstName(displayName: string): string {
  const trimmed = displayName.trim();
  if (!trimmed) return "";
  return trimmed.split(/\s+/)[0];
}

/**
 * Build the default invitation message from selected user, role, project,
 * and sender. Returns "" when either the recipient or the role name is
 * missing. Generic over the role label — callers pass a bundle name
 * (governance grants) or a slot role label (operational assignments).
 *
 * Template lives in i18n keyed by the `templateKey` argument — typically
 * "access.oa.messageTemplate" (projectConfig namespace) or
 * "assignment.messageTemplate" (projectTeam namespace).
 *
 * The `fallbackProjectNameKey` is used when projectName is empty — points
 * to a localized "this project" string in the same namespace.
 */
export interface DefaultInviteMessageArgs {
  recipient: SecurityUserResponse | null;
  roleName: string;
  projectName: string | undefined;
  sender: SecurityUserResponse | null;
  t: (key: string, opts?: any) => string;
  templateKey: string;
  fallbackProjectNameKey: string;
}

export function computeDefaultInviteMessage(args: DefaultInviteMessageArgs): string {
  const {
    recipient,
    roleName,
    projectName,
    sender,
    t,
    templateKey,
    fallbackProjectNameKey,
  } = args;
  if (!recipient || !roleName) return "";
  const firstName =
    extractFirstName(recipient.displayName || recipient.name || "");
  const projectLabel =
    projectName?.trim() || t(fallbackProjectNameKey);
  const senderName = sender?.displayName || sender?.name || "";
  return t(templateKey, {
    firstName,
    projectName: projectLabel,
    roleName,
    senderName,
  });
}

// ── Component ──────────────────────────────────────────────────

export interface UserIdentityCardProps {
  user: SecurityUserResponse;
  homeCommunityName: string | null;
  /** Per-dimension tier levels from useApiImpactSignature. */
  tiers: Record<string, any> | null;
  /** Hook is still fetching — render shimmer in the radar pane. */
  loading: boolean;
  /**
   * When true, show the "no 2FA yet" pill inline with the name in the
   * banner. Controlled by the parent: only true when the selected bundle
   * or slot requires 2FA AND the user lacks TOTP enrollment. Avoids
   * permanent 2FA-shaming when 2FA isn't relevant.
   */
  showNo2faPill: boolean;
  /**
   * Optional "Change person" affordance. Only passed in add mode — edit
   * mode locks the user identity to whoever the existing grant/assignment
   * points at.
   */
  onClear?: () => void;
  /**
   * i18n key for the optional "no 2FA yet" pill label. Required when
   * showNo2faPill is true. Each consumer (projectConfig OA, projectTeam
   * assignment) brings its own key in its own namespace.
   */
  pillNo2faKey: string;
  /** i18n tooltip key for the pill. */
  pillNo2faTooltipKey: string;
  /** i18n key for the "Change person" button label (add mode only). */
  changePersonKey: string;
  /** i18n key with {{name}} interpolation for the home community line. */
  homeCommunityLineKey: string;
  /** i18n key for the "no home community set" fallback. */
  noHomeCommunityKey: string;
  /** i18n key for the impact-loading aria label. */
  impactLoadingKey: string;
  /** i18n key for the impact-unavailable line. */
  impactUnavailableKey: string;
  /** Translation function from useAppTranslation. */
  t: (key: string, opts?: any) => string;
}

export function UserIdentityCard({
  user,
  homeCommunityName,
  tiers,
  loading,
  showNo2faPill,
  onClear,
  pillNo2faKey,
  pillNo2faTooltipKey,
  changePersonKey,
  homeCommunityLineKey,
  noHomeCommunityKey,
  impactLoadingKey,
  impactUnavailableKey,
  t,
}: UserIdentityCardProps) {
  const displayName = user.displayName || user.name || "—";
  const initials = computeInitials(displayName);
  const hasImage = !!user.profileImageUrl && user.profileImageUrl.trim() !== "";

  return (
    <div className={styles.card}>
      {/* Banner: name (+ optional pill), email, home community */}
      <div className={styles.banner}>
        <div className={styles.name}>
          <span>{displayName}</span>
          {showNo2faPill && (
            <span
              className={styles.pillNo2fa}
              title={t(pillNo2faTooltipKey)}
            >
              {t(pillNo2faKey)}
            </span>
          )}
        </div>
        {user.email && (
          <div className={styles.email}>{user.email}</div>
        )}
        {homeCommunityName ? (
          <div className={styles.community}>
            {t(homeCommunityLineKey, { name: homeCommunityName })}
          </div>
        ) : (
          <div className={`${styles.community} ${styles.communityMuted}`}>
            {t(noHomeCommunityKey)}
          </div>
        )}
      </div>

      {/* Twin panes: avatar on the inline-start, radar on the inline-end.
          Borderless per Skip's lock — each pane carries its own visual
          weight without needing a frame. */}
      <div className={styles.panes}>
        <div className={styles.pane}>
          {hasImage ? (
            <img
              src={mediaSrc(user.profileImageUrl) ?? undefined}
              alt={displayName}
              className={styles.avatar}
            />
          ) : (
            <div className={styles.avatarPlaceholder}>
              {initials}
            </div>
          )}
        </div>
        <div className={styles.pane}>
          {loading ? (
            <div
              className={styles.radarShimmer}
              aria-label={t(impactLoadingKey)}
            />
          ) : tiers ? (
            <ImpactSignatureMini tiers={tiers} interactive={false} />
          ) : (
            <div className={styles.radarUnavailable}>
              {t(impactUnavailableKey)}
            </div>
          )}
        </div>
      </div>

      {/* "Change person" affordance — only in add mode, bottom-right. */}
      {onClear && (
        <div className={styles.cardFooter}>
          <button
            type="button"
            className={styles.clearBtn}
            onClick={onClear}
          >
            {t(changePersonKey)}
          </button>
        </div>
      )}
    </div>
  );
}
